import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CustomerRoleCreateRequest, CustomerRoleUpdateRequest } from '../types/role';
import { useCustomersApi } from './use-customers-api';

const CUSTOMER_ROLES_QUERY_KEY = ['customer-roles'] as const;
const CUSTOMER_ROLE_QUERY_KEY = (slug: string) => ['customer-role', slug] as const;

export function useCustomerRoles() {
  const { customersApi } = useCustomersApi();
  return useQuery({
    queryKey: CUSTOMER_ROLES_QUERY_KEY,
    queryFn: () => customersApi.roles().list(),
  });
}

export function useCustomerRole(slug: string | undefined) {
  const { customersApi } = useCustomersApi();
  return useQuery({
    queryKey: CUSTOMER_ROLE_QUERY_KEY(slug ?? ''),
    queryFn: () => customersApi.roles().get(slug ?? ''),
    enabled: !!slug,
  });
}

export function useCreateCustomerRole() {
  const { customersApi } = useCustomersApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CustomerRoleCreateRequest) => customersApi.roles().create(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CUSTOMER_ROLES_QUERY_KEY });
    },
  });
}

export function useUpdateCustomerRole(slug: string) {
  const { customersApi } = useCustomersApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CustomerRoleUpdateRequest) => customersApi.roles().update(slug, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CUSTOMER_ROLES_QUERY_KEY });
      void queryClient.invalidateQueries({
        queryKey: CUSTOMER_ROLE_QUERY_KEY(slug),
      });
    },
  });
}

export function useDeleteCustomerRole() {
  const { customersApi } = useCustomersApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => customersApi.roles().delete(slug),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CUSTOMER_ROLES_QUERY_KEY });
    },
  });
}
